import { initialize, updateUser, type WithJWT } from '@iterable/web-sdk'
import { captureException } from '@sentry/react'
import { getIterableToken } from 'api/generated/iterable/iterable'
import { ITERABLE_DISMISSED_FIELD } from './dismissedAnnouncements'
import env from 'env.json'

/**
 * The Iterable Web SDK connection, mirroring `lib/segment/tracking`: one lazily-built singleton,
 * every call wrapped so a marketing SDK can never take the dashboard down with it.
 *
 * Lazy rather than module-scope (which is how Segment and App Insights do it here) for two
 * reasons: `initialize` installs axios interceptors and reads `localStorage`, and nothing should
 * happen on import in a test file that merely pulls in a component; and there is no point opening
 * a connection for a signed-out visitor, since every embedded-messaging call needs a provider JWT.
 */
let client: WithJWT | undefined

/**
 * Fetches the short-lived JWT Iterable needs, from our own backend (QMD-4691).
 *
 * The SDK owns refresh from here: it re-calls this within a minute of expiry, on a 401, and on
 * sign-in, so nothing here schedules anything.
 *
 * `AccessToken` is generated as `string | null` — the backend contract marks it `required` *and*
 * `nullable` — so it is narrowed rather than passed through. Handing `null` to the SDK would be
 * stored as the auth token and produce 401s on every later call instead of failing here.
 */
async function generateJWT(): Promise<string> {
  const { AccessToken } = await getIterableToken()
  if (!AccessToken) throw new Error('Iterable token endpoint returned no access token')
  return AccessToken
}

function getClient(): WithJWT {
  client ??= initialize(env.iterableApiKey, generateJWT)
  return client
}

/**
 * Identity changes run in order, because `setUserID` is slow (it fetches a JWT) and `logout` is
 * instant.
 *
 * Without the queue, signing out while a `setUserID` was still in flight let that call land
 * *after* the logout and re-identify the provider who had just left — the SDK would then hold a
 * signed-out provider's identity, and the stale `generateJWT` callback would keep refreshing a
 * token for them. `wanted` is the identity most recently asked for, so a `setUserID` that has
 * been superseded while queued is skipped rather than applied late.
 */
let queue: Promise<void> = Promise.resolve()
let wanted: string | null = null

/**
 * Whether the SDK currently has a user set, and a subscription to that changing.
 *
 * Callers need this because the SDK refuses — and then silently swallows — every embedded-messaging
 * request made before `setUserID` has landed: `baseIterableRequest` rejects while its internal
 * `getTypeOfAuth()` is null, and `IterableEmbeddedManager.retrieveEmbeddedMessages` catches that
 * rejection and returns without notifying anyone. A fetch issued too early therefore produces no
 * messages, no error and no Sentry event, and nothing retries until the next poll.
 *
 * Identity is two network round trips behind a mount (the provider profile, then the JWT), so
 * "wait for this" is the only way a placement can fetch exactly once, at the first moment it can
 * succeed.
 */
let isIdentified = false
const identityListeners = new Set<() => void>()

function publishIdentity(next: boolean): void {
  if (isIdentified === next) return
  isIdentified = next
  identityListeners.forEach((listener) => listener())
}

export function getIsIterableIdentified(): boolean {
  return isIdentified
}

/**
 * Who the SDK is currently identified as, or `null` when signed out.
 *
 * `wanted` rather than a second field: while a `setUserID` is still queued the two can disagree,
 * and the answer callers need is "who is this *going* to be", so that per-provider state is filed
 * under the provider who asked for it rather than the one being replaced.
 */
export function getIterableUserId(): string | null {
  return wanted
}

export function subscribeToIterableIdentity(listener: () => void): () => void {
  identityListeners.add(listener)
  return () => {
    identityListeners.delete(listener)
  }
}

/**
 * Identifies the signed-in provider to Iterable.
 *
 * `userId` is the provider's global key — the same value the backend puts in the JWT's `userId`
 * claim, surfaced on the frontend as `profile.ProviderId`. Deliberately **not** email: the
 * provider Iterable project keys on the global key, and an opaque id keeps PII out of the SDK's
 * localStorage.
 */
export function setIterableUser(userId: string): Promise<void> {
  wanted = userId
  queue = queue.then(async () => {
    if (wanted !== userId) return
    try {
      await getClient().setUserID(userId)
      publishIdentity(true)
    } catch (error) {
      captureException(error)
    }
  })
  return queue
}

/** Clears the Iterable identity on sign-out, alongside `tracking.reset()`. */
export function clearIterableUser(): Promise<void> {
  wanted = null
  queue = queue.then(() => {
    publishIdentity(false)
    try {
      client?.logout()
    } catch (error) {
      captureException(error)
    }
  })
  return queue
}

/**
 * Mirrors a provider's dismissals onto their Iterable profile, so the campaigns they have waved
 * away can be excluded by eligibility rather than only hidden by this browser.
 *
 * **Inert on its own.** Writing the field changes nothing until each campaign's eligibility list
 * carries a `does not contain` criterion for its own id — see `ITERABLE_DISMISSED_FIELD`. This is
 * the cross-device half of dismissal; the local record is the half that always works.
 *
 * Fire-and-forget, and only when a client already exists: a dismissal is not worth building an SDK
 * connection for, and a failed profile write costs the provider nothing on this device.
 */
export function publishDismissedCampaigns(campaignIds: string[]): void {
  if (!client) return
  updateUser({ dataFields: { [ITERABLE_DISMISSED_FIELD]: campaignIds } }).catch((error: unknown) =>
    captureException(error),
  )
}

/** Test seam: drops the memoised client and identity queue so each test starts clean. */
export function resetIterableClientForTests(): void {
  client = undefined
  queue = Promise.resolve()
  wanted = null
  isIdentified = false
  identityListeners.clear()
}
